import { TZDateMini } from '@date-fns/tz';
import { getDaysInMonth, isValid, startOfDay } from 'date-fns';
import { isNil } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { getTimezone } from '@dynatrace-sdk/user-preferences';

import { getPlaceholder } from '../../../core/time/useDateTimeMessages.js';
import type { TimeValue } from '../../../core/types/time.js';
import { replaceSecondFractions } from '../../forms-core/time/date-time.js';
import {
  convertDateOnlyToDateTime,
  getDayPeriodValues,
} from '../../forms-core/time/helpers.js';
import { getTimeValue } from '../../forms-core/time/timeParser.js';
import { SpinButtonGroup } from '../../spin-buttons/spin-button-group/SpinButtonGroup.js';
import type { SpinButtonHandler } from '../../spin-buttons/spin-button-group/types.js';
import type { DateTimePickerProps } from '../types.js';
import { useDateTimeFormat } from './useDateTimeFormat.js';
import type { NumberSpinButtonProps } from '../../spin-buttons/number-spin-button/types.js';
import type { TextSpinButtonProps } from '../../spin-buttons/text-spin-button/types.js';

/**
 * Returns a list of preconfigured spinbuttons for a given datetime and format configuration.
 */
export function useSpinButtons(
  isoDateTime: TimeValue | string | null,
  type: DateTimePickerProps['type'],
  precision: DateTimePickerProps['precision'],
  isDisabled: boolean,
  isReadonly: boolean,
  ariaLabelPrefix: string | undefined,
  value?: TimeValue | string | null,
  onPartChange?: (dateTime: Date | null | undefined) => void,
  onPartClear?: () => void,
) {
  const intl = useIntl();
  const prefix = ariaLabelPrefix ? `${ariaLabelPrefix} ` : '';

  const { toParts, toDateTime, emptyParts } = useDateTimeFormat(
    type,
    precision,
  );
  const timeValue = useMemo(() => getTimeValue(isoDateTime), [isoDateTime]);
  const dayPeriodValues = useMemo(() => getDayPeriodValues(), []);
  const timezone = getTimezone();

  const [parts, setParts] = useState(
    timeValue !== null && timeValue !== undefined
      ? toParts(
          new TZDateMini(
            convertDateOnlyToDateTime(timeValue.absoluteDate, type),
            timezone,
          ),
        )
      : emptyParts,
  );
  const hasDayPeriod = useMemo(
    () => parts.some((p) => p.type === 'dayPeriod'),
    [parts],
  );

  const precisionRef = useRef(precision);

  // Kept in sync each render so a follow-up updatePart in the same tick
  // (bulk clear + applyText, key-repeat before commit) reads fresh parts.
  const partsRef = useRef(parts);
  partsRef.current = parts;

  // Reconstructs a datetime from a parts array. Preserves the anchor value's
  // seconds/milliseconds when the visible precision cannot express them, so
  // that editing a coarser part does not zero out hidden sub-precision.
  const computeDateTimeFromParts = useCallback(
    (
      newParts: Intl.DateTimeFormatPart[],
      previousPrecision: DateTimePickerProps['precision'],
    ): Date | null => {
      const newDateTime = toDateTime(newParts);
      if (!newDateTime || !isValid(newDateTime)) {
        return newDateTime;
      }
      if (precision === previousPrecision && precision === 'milliseconds') {
        return newDateTime;
      }
      const anchor = new TZDateMini(
        getTimeValue(value, precision)?.absoluteDate ??
          startOfDay(TZDateMini.tz(timezone)).toISOString(),
        timezone,
      );
      const milliseconds = anchor.getMilliseconds();
      const seconds = anchor.getUTCSeconds();
      if (
        precision !== previousPrecision ||
        precision === 'minutes' ||
        precision === undefined
      ) {
        newDateTime.setUTCSeconds(seconds);
      }
      return new TZDateMini(
        replaceSecondFractions(
          newDateTime.toISOString(),
          milliseconds.toString(),
        ),
        timezone,
      );
    },
    [toDateTime, precision, value, timezone],
  );

  const dateTime = useMemo(() => {
    const result = computeDateTimeFromParts(parts, precisionRef.current);
    precisionRef.current = precision;
    return result;
  }, [computeDateTimeFromParts, parts, precision]);

  const daysInMonth =
    dateTime !== null && isValid(dateTime) ? getDaysInMonth(dateTime) : 31;

  const updatePart = useCallback(
    (
      partType: Intl.DateTimeFormatPart['type'] | undefined,
      partValue: string,
    ) => {
      if (partType === undefined) {
        return;
      }
      const currentParts = partsRef.current;
      const index = currentParts.findIndex((p) => p.type === partType);
      if (index < 0 || currentParts[index].value === partValue) {
        return;
      }

      const newParts = [...currentParts];
      newParts[index] = { type: partType, value: partValue };
      partsRef.current = newParts;

      // Skip sync dispatch on clear so the passive [dateTime] effect can
      // coalesce a bulk clear into one final onChange, avoiding intermediate
      // 'Invalid Date' echoes. Signal the pending clear so the passive effect
      // runs even when a prior sync dispatch already set spinButtonChangeHandledRef.
      if (partValue !== '') {
        onPartChange?.(
          computeDateTimeFromParts(newParts, precisionRef.current),
        );
      } else {
        onPartClear?.();
      }
      setParts(newParts);
    },
    [computeDateTimeFromParts, onPartChange, onPartClear],
  );

  const sharedProps = {
    required: false,
    disabled: isDisabled,
    readOnly: isReadonly,
    isCyclic: true,
  };

  const yearProps: NumberSpinButtonProps = {
    ...sharedProps,
    displayFormat: {
      length: 4,
    },
    maxValue: 9999,
    minValue: 0,
    value: parseInt(parts.find((p) => p.type === 'year')?.value ?? '0'),
    onChange: (value) => {
      updatePart('year', value?.toString() ?? '');
    },
    'data-pattern': 'yyyy',
  };

  const monthProps: NumberSpinButtonProps = {
    ...sharedProps,
    displayFormat: {
      length: 2,
    },
    maxValue: 12,
    minValue: 1,
    value: parseInt(parts.find((p) => p.type === 'month')?.value ?? '0'),
    onChange: (value) => {
      const formerValue = parseInt(
        parts.find((p) => p.type === 'month')?.value ?? '0',
      );
      if (value !== formerValue) {
        updatePart('month', value?.toString() ?? '');
      }
    },
    'data-pattern': 'MM',
  };

  const dayProps: NumberSpinButtonProps = {
    ...sharedProps,
    displayFormat: {
      length: 2,
    },
    maxValue: daysInMonth,
    minValue: 1,
    value: parseInt(parts.find((p) => p.type === 'day')?.value ?? '0'),
    onChange: (value) => {
      updatePart('day', value?.toString() ?? '');
    },
    'data-pattern': 'dd',
  };

  const hourProps: NumberSpinButtonProps = {
    ...sharedProps,
    displayFormat: {
      length: 2,
    },
    maxValue: hasDayPeriod ? 12 : 23,
    minValue: hasDayPeriod ? 1 : 0,
    value: parseInt(parts.find((p) => p.type === 'hour')?.value ?? '0'),
    onChange: (value) => {
      updatePart('hour', value?.toString() ?? '');
    },
    'data-pattern': parts.find((p) => p.type === 'dayPeriod') ? 'hh' : 'HH',
  };

  const minuteProps: NumberSpinButtonProps = {
    ...sharedProps,
    displayFormat: {
      length: 2,
    },
    maxValue: 59,
    minValue: 0,
    value: parseInt(parts.find((p) => p.type === 'minute')?.value ?? '0'),
    onChange: (value) => {
      updatePart('minute', value?.toString() ?? '');
    },
    'data-pattern': 'mm',
  };

  const secondsProps: NumberSpinButtonProps = {
    ...sharedProps,
    displayFormat: {
      length: 2,
    },
    maxValue: 59,
    minValue: 0,
    value: parseInt(parts.find((p) => p.type === 'second')?.value ?? '0'),
    onChange: (value) => {
      updatePart('second', value?.toString() ?? '');
    },
    'data-pattern': 'ss',
  };

  const fractionalSecondProps: NumberSpinButtonProps = {
    ...sharedProps,
    displayFormat: {
      length: 3,
    },
    maxValue: 999,
    minValue: 0,
    value: parseInt(
      parts.find((p) => p.type === 'fractionalSecond')?.value ?? '0',
    ),
    onChange: (value) => {
      updatePart('fractionalSecond', value?.toString() ?? '');
    },
    'data-pattern': 'SSS',
  };

  const dayPeriodProps: TextSpinButtonProps = {
    ...sharedProps,
    options: dayPeriodValues,
    value: parts.find((p) => p.type === 'dayPeriod')?.value,
    onChange: (value) => {
      if (value === null) {
        updatePart?.('dayPeriod', '');
        return;
      }
      updatePart?.('dayPeriod', value);
    },
    'data-pattern': 'aa',
  };

  const { spinButtons, handlers } = useMemo(() => {
    // Keep a map of all the imperative handlers per spinbutton
    const handlers = new Map<
      Intl.DateTimeFormatPartTypes,
      SpinButtonHandler | null
    >();

    const setHandler = (
      handler: SpinButtonHandler | null,
      type: Intl.DateTimeFormatPart['type'],
    ) => {
      handlers?.set?.(type, handler);
    };

    // Create an array of preconfigured spinbuttons for each datetime part.
    const spinButtons = parts.map(({ type, value }, index) => {
      switch (type) {
        case 'year':
          return (
            <SpinButtonGroup.Number
              {...yearProps}
              key={type}
              ref={(handler: SpinButtonHandler | null) => {
                setHandler(handler, type);
              }}
              value={
                yearProps.value !== undefined
                  ? yearProps.value
                  : parseInt(value)
              }
              aria-label={intl.formatMessage(
                {
                  defaultMessage: '{prefix}year',
                  id: 'nZk5lg177+CQcO/Z',
                  description: 'The aria label for the year input of a date.',
                },
                {
                  prefix,
                },
              )}
              placeholder={getPlaceholder(type, intl.formatMessage)}
            />
          );
        case 'month':
          return (
            <SpinButtonGroup.Number
              {...monthProps}
              key={type}
              ref={(handler: SpinButtonHandler | null) => {
                setHandler(handler, type);
              }}
              value={
                monthProps.value !== undefined
                  ? monthProps.value
                  : parseInt(value)
              }
              aria-label={intl.formatMessage(
                {
                  defaultMessage: '{prefix}month',
                  id: 'LoBSMcHvTQEABp39',
                  description: 'The aria label for the month input of a date.',
                },
                {
                  prefix,
                },
              )}
              placeholder={getPlaceholder(type, intl.formatMessage)}
            />
          );
        case 'day':
          return (
            <SpinButtonGroup.Number
              {...dayProps}
              key={type}
              ref={(handler: SpinButtonHandler | null) => {
                setHandler(handler, type);
              }}
              value={
                dayProps.value !== undefined ? dayProps.value : parseInt(value)
              }
              aria-label={intl.formatMessage(
                {
                  defaultMessage: '{prefix}day',
                  id: 'Dz8K7VMW5UFpwetG',
                  description: 'The aria label for the day input of a date.',
                },
                {
                  prefix,
                },
              )}
              placeholder={getPlaceholder(type, intl.formatMessage)}
            />
          );
        case 'hour':
          return (
            <SpinButtonGroup.Number
              {...hourProps}
              key={type}
              ref={(handler: SpinButtonHandler | null) => {
                setHandler(handler, type);
              }}
              value={
                hourProps.value !== undefined
                  ? hourProps.value
                  : parseInt(value)
              }
              aria-label={intl.formatMessage(
                {
                  defaultMessage: '{prefix}hour',
                  id: 'DXoXEb27LMuapbC4',
                  description: 'The aria label for the hour input of a time.',
                },
                {
                  prefix,
                },
              )}
              placeholder={getPlaceholder(type, intl.formatMessage)}
            />
          );
        case 'minute':
          return (
            <SpinButtonGroup.Number
              {...minuteProps}
              key={type}
              ref={(handler: SpinButtonHandler | null) => {
                setHandler(handler, type);
              }}
              value={
                minuteProps.value !== undefined
                  ? minuteProps.value
                  : parseInt(value)
              }
              aria-label={intl.formatMessage(
                {
                  defaultMessage: '{prefix}minute',
                  id: 'pGR/6J+2uPcvCSju',
                  description: 'The aria label for the minute input of a time.',
                },
                {
                  prefix,
                },
              )}
              placeholder={getPlaceholder(type, intl.formatMessage)}
            />
          );
        case 'second':
          return (
            <SpinButtonGroup.Number
              {...secondsProps}
              key={type}
              ref={(handler: SpinButtonHandler | null) => {
                setHandler(handler, type);
              }}
              value={
                secondsProps.value !== undefined
                  ? secondsProps.value
                  : parseInt(value)
              }
              aria-label={intl.formatMessage(
                {
                  defaultMessage: '{prefix}second',
                  id: 'WD6kswCbAgSXwMnO',
                  description: 'The aria label for the second input of a time.',
                },
                {
                  prefix,
                },
              )}
              placeholder={getPlaceholder(type, intl.formatMessage)}
            />
          );
        case 'fractionalSecond':
          return (
            <SpinButtonGroup.Number
              {...fractionalSecondProps}
              key={type}
              ref={(handler: SpinButtonHandler | null) => {
                setHandler(handler, type);
              }}
              value={
                fractionalSecondProps.value !== undefined
                  ? fractionalSecondProps.value
                  : parseInt(value)
              }
              aria-label={intl.formatMessage(
                {
                  defaultMessage: '{prefix}millisecond',
                  id: 'wa4ct2vRPvqd0Uif',
                  description:
                    'The aria label for the millisecond input of a time.',
                },
                {
                  prefix,
                },
              )}
              placeholder={getPlaceholder(type, intl.formatMessage)}
            />
          );
        case 'dayPeriod':
          return (
            <SpinButtonGroup.Text
              {...dayPeriodProps}
              key={type}
              ref={(handler: SpinButtonHandler | null) => {
                setHandler(handler, type);
              }}
              value={
                dayPeriodProps.value !== undefined
                  ? dayPeriodProps.value
                  : value
              }
              aria-label={intl.formatMessage(
                {
                  defaultMessage: '{prefix}day period',
                  id: 'a9uVGc0cPE1OqAJj',
                  description:
                    'The aria label for the day period (am/pm) input of a time.',
                },
                {
                  prefix,
                },
              )}
              /* e.g. "AM/PM" - should be localized accordingly */
              placeholder={getPlaceholder(type, intl.formatMessage)}
            />
          );
        case 'literal':
          return isNil(value) ? null : (
            // Show the whole literal but only use the first char as trigger.
            <SpinButtonGroup.Separator
              key={`${type}_${index}`}
              trigger={value[0]}
            >
              {value}
            </SpinButtonGroup.Separator>
          );
        default:
          // For unknown part types, simply render the value as textnode in the DOM.
          return value;
      }
    });

    return { handlers, spinButtons };
    // Do not recreate the spinbuttons on parts update. This prevents prefix issues when starting values with 0.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isDisabled,
    isReadonly,
    updatePart,
    daysInMonth,
    hasDayPeriod,
    parts.length,
    yearProps.value,
    monthProps.value,
    dayProps.value,
    hourProps.value,
    minuteProps.value,
    secondsProps.value,
    fractionalSecondProps.value,
    dayPeriodProps.value,
  ]);

  // Callback to push the given part values to the corresponding spinbuttons.
  const updateParts = useCallback(
    (updatedParts: Intl.DateTimeFormatPart[]) => {
      const nonLiteralParts = updatedParts.filter(
        ({ type }) => type !== 'literal',
      );

      const lookup = new Map(
        nonLiteralParts.map(({ type, value }) => [type, value]),
      );

      // Apply the new values to the spinbuttons.
      // Would not be necessary, if the spinbuttons would be controlled. But this is not possible because this
      // interferes with the expected typing behavior for spinbuttons with format patterns like for this one used for the datetime parts.
      nonLiteralParts.forEach((part) => {
        const handler = handlers.get(part.type);
        if (part.value === '') {
          handler?.clearValue();
        } else if (handler?.type === 'number') {
          handler.applyText(part.value);
        } else if (handler?.type === 'text') {
          handler.searchValue(part.value[0]);
        }
      });

      // update the parts with new values
      setParts((prevParts) => {
        return prevParts.map((part) => {
          const newValue = lookup.get(part.type);
          return newValue !== undefined ? { ...part, value: newValue } : part;
        });
      });
    },
    [handlers],
  );

  // If the format, type or precision is changing, restore the prev. values when generating a set of new spinbuttons.
  useEffect(() => {
    setParts(() =>
      dateTime === null || !isValid(dateTime) ? emptyParts : toParts(dateTime),
    );
    // Need to update the parts on precision change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emptyParts, toDateTime, toParts, precision]);

  return {
    spinButtons,
    dateTime,
    updateParts,
    handlers,
    parts,
    toDateTime,
  };
}
